import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/components/PublicLayout";
import { OtpInput } from "@/components/OtpInput";

export function ForgotPassword({ dev = false }) {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email, portal: dev ? "dev" : "lab" }); setStep(2); toast.success("If the account exists, a code has been sent"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const reset = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { email, otp, new_password: pw, portal: dev ? "dev" : "lab" }); toast.success("Password set. Please sign in."); nav(dev ? "/qlog" : "/login"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title={dev ? "Q log" : "Reset or activate your password"} subtitle={dev ? "" : "Invited team members use this page to set their first password."}>
      {step === 1 ? (
        <form onSubmit={send} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-1.5"><Label>Registered email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          <Button disabled={busy} className="w-full bg-[#48217A] hover:bg-[#3B1A66]" data-testid="forgot-send-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send code</Button>
          <Link to={dev ? "/qlog" : "/login"} className="block text-center text-xs text-[#48217A] hover:underline" data-testid="forgot-back-link">Back to sign in</Link>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-4" data-testid="reset-form">
          <Label>Code sent to {email}</Label>
          <OtpInput value={otp} onChange={setOtp} testid="reset-otp-input" />
          <button type="button" disabled={busy} onClick={send} className="text-xs text-[#48217A] hover:underline" data-testid="reset-resend-btn">Resend code</button>
          <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></div>
          <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-confirm-input" /></div>
          <Button disabled={busy || otp.length !== 6} className="w-full bg-[#2E9B65] hover:bg-[#258254]" data-testid="reset-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Set password</Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ChangePassword() {
  const nav = useNavigate();
  const { user, reload, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password changed");
      await reload();
      nav(user?.role === "super_admin" ? "/admin" : user?.role === "developer" ? "/dev" : "/dashboard");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout onBack={user?.must_change_password ? async () => { await logout(); nav("/"); } : undefined} title="Change your password" subtitle={user?.must_change_password ? "For security, you must set a new password before continuing." : "Update your password."}>
      <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
        <div className="space-y-1.5"><Label>Current password</Label><Input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="change-current-input" /></div>
        <div className="space-y-1.5"><Label>New password (min 8 characters)</Label><Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="change-new-input" /></div>
        <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="change-confirm-input" /></div>
        <Button disabled={busy} className="w-full bg-[#48217A] hover:bg-[#3B1A66]" data-testid="change-password-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Update password</Button>
      </form>
    </AuthLayout>
  );
}
